// src/components/Profile.tsx
import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { text } from 'stream/consumers';

const ProfilePageContainer = styled(motion.div)`
  padding: 0px 20px;
  color: #f0f0f0;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Arial', sans-serif;
`;

const PageTitle = styled.h1`
  font-size: 2.8em;
  text-align: center;
  margin-bottom: 40px;
  color: cyan;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
`;

const Section = styled.section`
  margin-bottom: 30px;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const SectionTitle = styled.h2`
  font-size: 1.8em;
  color: #00bcd4; /* 明るめの青緑 */
  margin-bottom: 15px;
  border-bottom: 2px solid #00bcd4;
  padding-bottom: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr; /* ラベルと値の列 */
  gap: 12px 20px; /* 行間と列間 */
  align-items: start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr; /* スマホでは1列に */
    gap: 8px;
  }
`;

const InfoItem = styled.div`
  display: contents; /* グリッドレイアウトを子要素に適用 */
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: #a0a0a0; /* やや明るめのグレー */
`;

const InfoValue = styled.span`
  line-height: 1.6;
  white-space: pre-wrap; /* 改行を保持 */
`;

const SubInfo = styled.p`
  font-size: 0.9em;
  color: #b0b0b0;
  margin-top: 5px;
  line-height: 1.5;
`;

const BusinessList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-top: 0;
`;

const BusinessListItem = styled.li`
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  &:before {
    content: '✓'; /* チェックマーク */
    color: #00bcd4;
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const companyProfile = {
  companyName: '合同会社Ludyworks',
  representatives: '木田貴之',
  headOfficeAddress: '大阪府東大阪市吉松1-4-21',
  headOfficeNote: 'SDGｓへの取り組みの一環として、オフィスへの訪問や紙の資料の送付は原則お断りしております。オンラインミーティングやクラウドストレージでの共有などをご活用ください。',
  branchOffices: [
    { name: '沖縄営業所', type: 'バーチャルオフィス', address: '' },
    { name: '梅田営業所', type: 'シェアオフィス', address: '大阪府大阪市北区豊崎３丁目１５−５ ＴＫビル' },
  ],
  establishmentYear: '2024年2月',
  capital: '60万円',
  businessContents: [
    '広告運用代行',
    'システム開発',
    '研修・セミナー講演',
  ],
  employeeCount: '2名',
  mainBank: 'GMOあおぞらネット銀行',
};

const Profile: React.FC = () => {
  return (
    <ProfilePageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <PageTitle>会社概要</PageTitle>

      <Section>
        <SectionTitle>基本情報</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>会社名</InfoLabel>
            <InfoValue>{companyProfile.companyName}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>代表者</InfoLabel>
            <InfoValue>{companyProfile.representatives}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>設立年</InfoLabel>
            <InfoValue>{companyProfile.establishmentYear}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>資本金</InfoLabel>
            <InfoValue>{companyProfile.capital}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>従業員数</InfoLabel>
            <InfoValue>{companyProfile.employeeCount}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>主要取引銀行</InfoLabel>
            <InfoValue>{companyProfile.mainBank}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>所在地</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>本社住所</InfoLabel>
            <div style={{ textAlign: 'left' }}>
              <InfoValue>{companyProfile.headOfficeAddress}</InfoValue>
              <SubInfo>{companyProfile.headOfficeNote}</SubInfo>
            </div>
          </InfoItem>
          <InfoItem>
            <InfoLabel>営業所</InfoLabel>
            <div style={{ textAlign: 'left' }}>
              {companyProfile.branchOffices.map((office, index) => (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <InfoValue>・{office.name} ({office.type})</InfoValue>
                  {office.address && <SubInfo style={{ marginLeft: '1em' }}>{office.address}</SubInfo>}
                </div>
              ))}
            </div>
          </InfoItem>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>事業内容</SectionTitle>
        <BusinessList>
          {companyProfile.businessContents.map((item, index) => (
            <BusinessListItem key={index}>{item}</BusinessListItem>
          ))}
        </BusinessList>
      </Section>

    </ProfilePageContainer>
  );
};

export default Profile;